import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { FRIENDS_METRICS, type FriendsMetric } from "../../model/FRIENDS_METRICS";
import type { FriendPlayer } from "../../model/FriendsBoard";
import { formatMetric } from "../../model/formatMetric";
import { METRIC_LABELS } from "../../model/METRIC_LABELS";
import { rankPlayers } from "../../model/rankPlayers";

const OF_TOTAL = msg({ message: "of {total}" });
const FIRST = msg({ message: "You're first among friends: {metric}" });
const PLACE = msg({ message: "You're #{place} among friends: {metric}" });
const BEHIND = msg({ message: "{name} is {gap} ahead. Behind you: {behind} of {others}." });
const LEAD = msg({ message: "Lead over second place: {gap}." });
const SUBRANKS = msg({ message: "{count, plural, one {# subrank} other {# subranks}}" });
const OUT_OF = msg({ message: "{count} of {total}" });
const MOVE_UP = msg({ message: "▲ {count, plural, one {# place} other {# places}}" });
const MOVE_DOWN = msg({ message: "▼ {count, plural, one {# place} other {# places}}" });

type PlaceCardProps = {
  players: readonly FriendPlayer[];
  meId: number;
  metric: FriendsMetric;
  // Сдвиг твоего места за неделю: плюс — поднялся; неизвестно — `null`.
  move: number | null;
};

// «Твоё место»: место по выбранному показателю, сколько не хватает до соседа выше,
// в скольких показателях ты первый и в верхней половине. Без места (нет матчей за период) —
// карточки нет.
export const PlaceCard = ({ players, meId, metric, move }: PlaceCardProps) => {
  const { i18n, t } = useLingui();
  const ranked = rankPlayers(players, metric).filter((row) => row.place !== null);
  const index = ranked.findIndex((row) => row.player.account_id === meId);
  const mine = ranked[index];
  if (mine === undefined || mine.value === null) {
    return null;
  }
  const metricName = i18n._(METRIC_LABELS[metric]);
  const gap = (a: number, b: number) =>
    metric === "rank"
      ? i18n._({ ...SUBRANKS, values: { count: Math.abs(a - b) } })
      : formatMetric(metric, Math.abs(a - b), i18n.locale);
  const above = ranked[index - 1];
  const second = ranked[1];
  let sub = "";
  if (above !== undefined && above.value !== null) {
    sub = i18n._({
      ...BEHIND,
      values: {
        name: above.player.display_name ?? t`Friend`,
        gap: gap(above.value, mine.value),
        behind: ranked.length - index - 1,
        others: ranked.length - 1,
      },
    });
  } else if (second !== undefined && second.value !== null) {
    sub = i18n._({ ...LEAD, values: { gap: gap(mine.value, second.value) } });
  }

  let firsts = 0;
  let topHalf = 0;
  for (const option of FRIENDS_METRICS) {
    const rows = rankPlayers(players, option).filter((row) => row.place !== null);
    const at = rows.findIndex((row) => row.player.account_id === meId);
    if (at === 0) {
      firsts += 1;
    }
    if (at >= 0 && at < rows.length / 2) {
      topHalf += 1;
    }
  }
  const total = FRIENDS_METRICS.length;
  let moveText = t`no change`;
  if (move !== null && move > 0) {
    moveText = i18n._({ ...MOVE_UP, values: { count: move } });
  } else if (move !== null && move < 0) {
    moveText = i18n._({ ...MOVE_DOWN, values: { count: -move } });
  }
  const tiles = [
    { label: t`First place`, value: i18n._({ ...OUT_OF, values: { count: firsts, total } }) },
    { label: t`Top half`, value: i18n._({ ...OUT_OF, values: { count: topHalf, total } }) },
    { label: t`This week`, value: moveText },
  ];

  return (
    <section
      aria-label={t`Your place`}
      className="flex flex-wrap items-center gap-x-7 gap-y-4 rounded-xl border border-border-strong bg-surface-raised px-5 py-5"
    >
      <div className="flex size-[76px] shrink-0 flex-col items-center justify-center rounded-full border-4 border-accent">
        <span className="font-mono text-[26px] leading-none font-semibold">{mine.place}</span>
        <span className="text-[11px] text-muted">
          {i18n._({ ...OF_TOTAL, values: { total: ranked.length } })}
        </span>
      </div>
      <div className="flex flex-[1_1_320px] flex-col gap-1">
        <p className="text-[19px] font-semibold">
          {mine.place === 1
            ? i18n._({ ...FIRST, values: { metric: metricName } })
            : i18n._({ ...PLACE, values: { place: mine.place, metric: metricName } })}
        </p>
        {sub !== "" && <p className="text-sm text-muted">{sub}</p>}
      </div>
      <dl className="flex flex-wrap gap-2.5">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className="flex min-w-[120px] flex-col rounded-lg border border-border bg-surface px-3 py-2"
          >
            <dt className="text-xs text-subtle">{tile.label}</dt>
            <dd className="font-mono text-lg font-semibold">{tile.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};
